import { createApiClient } from './app/api.js';
import { omitFileList } from './lib/file-list-request.js';
import { createMenuController } from './features/menu.js';
import { createOutlineState, rebuildOutlineState } from './features/outline-state.js';
import { renderPublicOutline } from './features/public-outline-renderer.js';
import { createThemeController } from './features/theme.js';
import { createTranslator, readI18n } from './lib/i18n.js';
import { showImagePopup } from './lib/image-popup.js';
import { createTagBrowserController } from './features/tag-browser.js';
import { getOutlineChildren } from './features/outline-state.js';
import { createNodeFontSizeController, createNodeLineHeightController } from './features/font-size.js';
import { createAppearanceDialogController } from './features/change-design.js';
import { createCardViewController } from './features/card-view.js';
import { formatMarkdownInline } from './lib/text-format.js';
import { renderMermaidBlocks } from './lib/mermaid-render.js';
import { byId, closestFrom, query, queryAll } from './lib/dom.js';

(function () {
  'use strict';

  const shell = query('.public-app-shell');
  const outlineEl = document.getElementById('outline');
  const currentFileNameEl = document.getElementById('current-file-name');
  const copyBar = document.getElementById('public-copy-bar');
  const copyButton = byId<HTMLButtonElement>('public-copy-button');
  const copyLoginLink = document.getElementById('public-copy-login-link');
  const copyStatus = document.getElementById('public-copy-status');
  const favoriteButton = byId<HTMLButtonElement>('topbar-favorite-button');
  const cardViewEl = document.getElementById('card-view');
  const cardBoardEl = document.getElementById('card-view-board');
  const cardDeckBarEl = document.getElementById('card-view-deck-bar');
  const viewModeButton = document.getElementById('topbar-view-mode-button');
  const menuToggleButton = document.getElementById('menu-toggle-button');
  const menuPanel = document.getElementById('topbar-menu-panel');
  const fontSizeOpenButton = document.getElementById('font-size-open-button');
  const fontSizePanel = document.getElementById('font-size-panel');
  const fontSizeCloseButton = document.getElementById('font-size-close-button');
  const fontSizeSlider = byId<HTMLInputElement>('font-size-slider');
  const fontSizeValue = document.getElementById('font-size-value');
  const fontSizeResetButton = document.getElementById('font-size-reset-button');
  const lineHeightSlider = byId<HTMLInputElement>('line-height-slider');
  const lineHeightValue = document.getElementById('line-height-value');
  const lineHeightResetButton = document.getElementById('line-height-reset-button');
  const csrfToken = query<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';
  const publicToken = shell?.dataset.publicToken || '';
  const viewCountDelayMs = Number(shell?.dataset.viewCountDelay || 3000);
  const i18n = readI18n();
  const t = createTranslator(i18n);
  const { apiGet, apiPost } = createApiClient({ csrfToken, editSessionToken: '' });
  // 初期化前に renderAll() が呼ばれることがあるため、宣言だけ先に置く
  let cardViewController = null;
  let tagBrowserController = null;
  const state = createOutlineState();
  // 公開画面は読み取り専用。カードビューも編集操作を出さない。
  state.readonly = true;
  state.inlineImagesEnabled = true;
  state.viewerFavorited = false;
  state.fontSizeLastFocus = null;
  const { setMenuOpen, toggleMenu } = createMenuController({
    menuPanel,
    menuToggleButton,
    closeNodeMenus: () => {},
  });
  const themeController = createThemeController({
    onThemeChange: () => renderAll(),
  });
  const nodeFontSizeController = createNodeFontSizeController({
    slider: fontSizeSlider,
    valueLabel: fontSizeValue,
    resetButton: fontSizeResetButton,
  });
  const nodeLineHeightController = createNodeLineHeightController({
    slider: lineHeightSlider,
    valueLabel: lineHeightValue,
    resetButton: lineHeightResetButton,
  });

  if (!shell || !outlineEl || !publicToken) {
    return;
  }

  themeController.applyTheme('outline-light');
  nodeFontSizeController.applyStoredNodeFontSize();
  nodeLineHeightController.applyStoredNodeLineHeight();

  function syncModalLock() {
    const tagBrowserPanel = document.getElementById('tag-browser-panel');
    document.body.classList.toggle(
      'has-modal',
      Boolean((tagBrowserPanel && !tagBrowserPanel.hidden) || (fontSizePanel && !fontSizePanel.hidden))
    );
  }

  // カードビュー: ノートに保存された表示モードを、公開画面でも同じように使う。
  // 公開画面は読み取り専用なので、編集系のコールバックは呼ばれない。
  const noop = () => {};
  cardViewController =
    cardViewEl && cardBoardEl
      ? createCardViewController({
          state,
          outlineEl,
          cardViewEl,
          cardBoardEl,
          deckBarEl: cardDeckBarEl,
          viewModeButton,
          getChildren: (parentId) => getOutlineChildren(state, parentId),
          activeFileName: () => state.files[0]?.name || '',
          // 閲覧専用なので、カードの本文は編集画面の原文ではなく整形済みの表示にする
          formatEditorMarkdown: (text) =>
            formatMarkdownInline(text, {
              inlineImages: true,
              blockLines: true,
            }),
          openTagBrowser: (tag) => {
            state.selectedTag = tag;
            return tagBrowserController?.setTagBrowserOpen(true);
          },
          applyNodeText: noop,
          runNodeTextPlugin: noop,
          createChildNode: noop,
          deleteNode: noop,
          moveNode: noop,
          copyNodeToClipboard: noop,
          moveNodeToDone: noop,
          // 表示モードはノートの持ち主の設定。閲覧者の切り替えは一時的なものとして保存しない。
          readStoredViewMode: () => {
            const file = state.files[0];
            if (file?.view_mode !== 'card') {
              return 'outline';
            }
            return file.card_layout === 'grid' ? 'grid' : 'card';
          },
          rememberViewMode: noop,
          afterRenderDetail: (dialogEl) => {
            if (dialogEl) {
              renderMermaidBlocks(dialogEl);
            }
          },
          syncModalLock,
          setMenuOpen,
          closeNodeMenus: () => window.closePublicNodeMenus(),
          t,
        })
      : null;

  function renderAll() {
    renderPublicOutline({ state, outlineEl, t });
    cardViewController?.render();
  }

  const fontSizeDialogController = createAppearanceDialogController({
    state,
    panel: fontSizePanel,
    closeButton: fontSizeCloseButton,
    titleSelector: '#font-size-title',
    titleKey: 'font_size',
    closeLabelKey: 'close_font_size',
    lastFocusKey: 'fontSizeLastFocus',
    menuToggleButton,
    syncModalLock,
    setMenuOpen,
    closeNodeMenus: () => window.closePublicNodeMenus(),
    t,
  });

  function setCopyStatus(text) {
    if (copyStatus) {
      copyStatus.textContent = text || '';
    }
  }

  function syncFavoriteButton() {
    if (!favoriteButton) {
      return;
    }
    const favorited = Boolean(state.viewerFavorited);
    const label = t(favorited ? 'favorite_remove' : 'favorite_add');
    favoriteButton.setAttribute('aria-pressed', favorited ? 'true' : 'false');
    favoriteButton.setAttribute('aria-label', label);
    favoriteButton.title = label;
  }

  async function toggleFavorite() {
    if (!favoriteButton) {
      return;
    }
    const next = !state.viewerFavorited;
    favoriteButton.disabled = true;
    try {
      const data = await apiPost('api/file_favorite.php', {
        public_token: publicToken,
        favorited: next,
      });
      state.viewerFavorited = Boolean(data.favorited);
    } catch (error) {
      console.error(error);
    } finally {
      favoriteButton.disabled = false;
      syncFavoriteButton();
    }
  }

  function showCopyControls(data) {
    if (!copyBar) {
      return;
    }
    copyBar.hidden = false;
    if (data.can_copy) {
      if (copyButton) copyButton.hidden = false;
      if (copyLoginLink) copyLoginLink.hidden = true;
      return;
    }
    if (copyButton) copyButton.hidden = true;
    if (copyLoginLink) copyLoginLink.hidden = Boolean(data.viewer_logged_in);
    if (data.viewer_logged_in) {
      copyBar.hidden = true;
    }
  }

  async function copyToMyOutline() {
    if (!copyButton) {
      return;
    }
    copyButton.disabled = true;
    setCopyStatus(t('public_copying'));
    try {
      const data = await apiPost('api/public_file_copy.php', { ...omitFileList, token: publicToken });
      setCopyStatus(t('public_copied'));
      window.location.href = `./?file_id=${encodeURIComponent(String(data.active_file_id))}`;
    } catch (error) {
      console.error(error);
      setCopyStatus(t('public_copy_failed'));
      copyButton.disabled = false;
    }
  }

  // アクセス数は、描画が終わってから少し遅らせて数える。
  // 読み込み途中で閉じられた分や、裏で開かれたままのタブは数えない。
  let viewCountSent = false;
  function scheduleViewCount() {
    if (viewCountSent) {
      return;
    }
    window.setTimeout(() => {
      if (viewCountSent) {
        return;
      }
      if (document.hidden) {
        // 裏のタブで開かれた場合は、表に出てから数える。
        document.addEventListener('visibilitychange', function onVisible() {
          if (document.hidden) {
            return;
          }
          document.removeEventListener('visibilitychange', onVisible);
          scheduleViewCount();
        });
        return;
      }
      viewCountSent = true;
      // 数えられなくても閲覧の邪魔はしないので、失敗しても何もしない。
      apiPost('api/public_view_count.php', { token: publicToken }).catch(() => {});
    }, viewCountDelayMs);
  }

  async function loadPublicOutline() {
    try {
      const data = await apiGet(`api/public_tree.php?t=${encodeURIComponent(publicToken)}`);
      state.files = data.files || [];
      state.viewerFavorited = Boolean(data.viewer_favorited);
      syncFavoriteButton();
      themeController.applyTheme(state.files[0]?.theme);
      state.activeFileId = data.active_file_id || null;
      rebuildOutlineState(state, data.nodes || []);
      if (currentFileNameEl && state.files[0]) {
        currentFileNameEl.textContent = state.files[0].name;
        currentFileNameEl.title = state.files[0].name;
        const appTitleEl = document.getElementById('app-title-link');
        const appTitle = appTitleEl?.textContent || 'Umibudo Outline';
        document.title = `${state.files[0].name} - ${appTitle}`;
      }
      cardViewController?.applyStoredViewMode();
      renderAll();
      showCopyControls(data);
      scheduleViewCount();
    } catch (error) {
      console.error(error);
      outlineEl.textContent = t('public_not_found_message');
    }
  }

  copyButton?.addEventListener('click', copyToMyOutline);
  favoriteButton?.addEventListener('click', toggleFavorite);
  fontSizeOpenButton?.addEventListener('click', fontSizeDialogController.open);
  fontSizeCloseButton?.addEventListener('click', fontSizeDialogController.close);
  fontSizeSlider?.addEventListener('input', () => {
    nodeFontSizeController.setNodeFontScale(Number(fontSizeSlider.value));
  });
  fontSizeResetButton?.addEventListener('click', () => {
    nodeFontSizeController.resetNodeFontSize();
  });
  lineHeightSlider?.addEventListener('input', () => {
    nodeLineHeightController.setNodeLineHeightScale(Number(lineHeightSlider.value));
  });
  lineHeightResetButton?.addEventListener('click', () => {
    nodeLineHeightController.resetNodeLineHeight();
  });
  fontSizePanel?.addEventListener('click', (event) => {
    if (event.target === fontSizePanel) {
      fontSizeDialogController.close();
    }
  });
  menuToggleButton?.addEventListener('click', (event) => {
    event.stopPropagation();
    toggleMenu();
  });
  document.addEventListener('click', (event) => {
    if (menuPanel?.hidden) {
      return;
    }
    if (event.target instanceof Node && menuPanel?.contains(event.target)) {
      return;
    }
    if (event.target instanceof Node && menuToggleButton?.contains(event.target)) {
      return;
    }
    setMenuOpen(false);
  });
  // 読み込みに失敗した画像は枠だけが残って紛らわしいので隠す。error は bubble しない。
  outlineEl.addEventListener(
    'error',
    (event) => {
      const img = event.target;
      if (img instanceof HTMLImageElement && img.dataset.mdZoomableImg) {
        img.closest('.md-image-preview-container')?.classList.add('is-load-error');
      }
    },
    true
  );

  outlineEl.addEventListener('click', (event) => {
    const zoomableImg =
      event.target instanceof Element
        ? closestFrom(event.target, 'img[data-md-zoomable-img], a[data-md-img-link]')
        : null;
    if (zoomableImg) {
      event.preventDefault();
      event.stopPropagation();
      const src =
        zoomableImg.tagName === 'A' ? (zoomableImg as HTMLAnchorElement).href : (zoomableImg as HTMLImageElement).src;
      const alt =
        zoomableImg.tagName === 'A' ? zoomableImg.textContent : (zoomableImg as HTMLElement).dataset.mdImageLabel;
      showImagePopup(src, alt || '', typeof t === 'function' ? t('close') || 'Close' : 'Close');
      return;
    }

    const link = event.target instanceof Element ? closestFrom(event.target, 'a.md-link') : null;
    if (!link) {
      return;
    }
    link.setAttribute('target', '_blank');
  });

  // 1. ノードのフルパスを配列で返す
  function nodePath(id) {
    const path = [];
    const seen = new Set();
    let currentId = id;
    while (currentId !== null && currentId !== undefined && !seen.has(currentId)) {
      seen.add(currentId);
      const node = state.nodes.get(currentId);
      if (!node) {
        break;
      }
      path.push(node);
      currentId = node.parent_id;
    }
    return path.reverse();
  }

  // 2. タグブラウザ表示用のノード簡易テキスト
  function shortNodeText(text) {
    const maxLength = 10;
    const normalized = String(text || '')
      .replace(/[\#\*\`\-]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const shortened = normalized.length > maxLength ? normalized.slice(0, maxLength) + '…' : normalized;
    return Array.from(shortened || '---').join('');
  }

  // 3. モーダル表示ロックのトグル
  // 4. 公開ノードメニューの表示・非表示
  window.togglePublicNodeMenu = function (id) {
    const row = query(`.node[data-id="${id}"]`, outlineEl);
    const panel = row ? query('.node-menu-panel', row) : null;
    const button = row ? query('.node-menu-button', row) : null;
    if (!row || !panel || !button) {
      return;
    }

    const shouldOpen = panel.hidden;
    window.closePublicNodeMenus(id);
    panel.classList.remove('is-above');
    panel.hidden = !shouldOpen;
    button.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');

    if (shouldOpen) {
      const buttonRect = button.getBoundingClientRect();
      if (buttonRect.bottom + 8 + 42 > window.innerHeight && buttonRect.top > 42) {
        panel.classList.add('is-above');
      }
      query('.node-menu-item', panel)?.focus();
    }
  };

  window.closePublicNodeMenus = function (exceptId = null) {
    for (const row of queryAll('.node', outlineEl)) {
      const id = Number(row.dataset.id);
      if (exceptId !== null && id === exceptId) {
        continue;
      }
      const panel = query('.node-menu-panel', row);
      const button = query('.node-menu-button', row);
      if (panel && button) {
        panel.hidden = true;
        panel.classList.remove('is-above');
        button.setAttribute('aria-expanded', 'false');
      }
    }
  };

  // 外側クリック時にノードメニューを閉じる
  document.addEventListener('click', (event) => {
    const menuBtn = closestFrom(event.target, '.node-menu-button');
    const menuPanel = closestFrom(event.target, '.node-menu-panel');
    if (!menuBtn && !menuPanel) {
      window.closePublicNodeMenus();
    }
  });

  // 5. タグブラウザのセットアップ
  const tagBrowserPanel = document.getElementById('tag-browser-panel');
  if (tagBrowserPanel) {
    const tagBrowserCloseButton = document.getElementById('tag-browser-close-button');
    const tagListEl = document.getElementById('tag-list');
    const tagResultsEl = document.getElementById('tag-results');
    const tagFilterClearButton = document.getElementById('tag-filter-clear-button');
    const tagSearchInput = byId<HTMLInputElement>('tag-search-input');
    const tagSortTabs = queryAll('[data-tag-sort]');

    tagBrowserController = createTagBrowserController({
      state,
      tagBrowserPanel,
      tagBrowserCloseButton,
      tagListEl,
      tagResultsEl,
      tagFilterClearButton,
      tagSearchInput,
      tagSortTabs,
      menuToggleButton,
      getChildren: (id) => getOutlineChildren(state, id),
      nodePath,
      shortNodeText,
      syncModalLock,
      setMenuOpen,
      closeNodeMenus: () => window.closePublicNodeMenus(),
      activeFilePayload: () => ({}),
      apiPost: async () => ({}),
      renderTree: () => {
        renderAll();
      },
      t,
    });

    tagBrowserCloseButton?.addEventListener('click', () => tagBrowserController.closeTagBrowser());
    tagFilterClearButton?.addEventListener('click', () => {
      state.selectedTag = null;
      tagBrowserController.renderTagBrowser();
      query('.tag-chip', tagListEl)?.focus();
    });

    tagBrowserPanel.addEventListener('click', (event) => {
      if (event.target === tagBrowserPanel) {
        tagBrowserController.closeTagBrowser();
        return;
      }

      const tagButton = closestFrom(event.target, '.tag-chip');
      if (tagButton) {
        state.selectedTag = tagButton.dataset.tag || null;
        tagBrowserController.renderTagBrowser();
        query('.tag-result-item', tagResultsEl)?.focus();
        return;
      }

      const resultButton = closestFrom(event.target, '.tag-result-item');
      if (resultButton) {
        tagBrowserController.focusNodeInTree(Number(resultButton.dataset.nodeId));
      }
    });

    // #topbar-tag-button のクリックで開く
    const topbarTagButton = document.getElementById('topbar-tag-button');
    topbarTagButton?.addEventListener('click', () => {
      tagBrowserController.setTagBrowserOpen(true);
    });

    // タグリンクをクリックした際のタグブラウザオープン
    outlineEl.addEventListener('click', (event) => {
      const tagButton = closestFrom(event.target, '.tag-inline');
      if (tagButton) {
        event.preventDefault();
        event.stopPropagation();
        state.selectedTag = tagButton.dataset.tag || null;
        tagBrowserController.setTagBrowserOpen(true);
      }
    });

    // グローバル公開 (ノードメニュー等から呼び出せるように)
    window.publicTagBrowser = {
      set selectedTag(val) {
        state.selectedTag = val;
      },
      setOpen: (isOpen) => {
        tagBrowserController.setTagBrowserOpen(isOpen);
      },
    };
  }

  // 6. 検索ボックスのセットアップ
  const searchInput = byId<HTMLTextAreaElement>('search-input');
  const searchClearButton = document.getElementById('search-clear-button');
  if (searchInput) {
    let searchTimeout = null;
    let composing = false;

    function scheduleSearch() {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      searchTimeout = setTimeout(() => {
        state.searchQuery = searchInput.value;
        renderAll();
      }, 150);
    }

    // 日本語入力の変換中は検索しない（未確定の文字で絞り込まない）
    searchInput.addEventListener('compositionstart', () => {
      composing = true;
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    });

    searchInput.addEventListener('compositionend', () => {
      composing = false;
      scheduleSearch();
    });

    searchInput.addEventListener('input', (event) => {
      // textarea なので、貼り付けで改行が混ざることがある
      if (searchInput.value.includes('\n')) {
        searchInput.value = searchInput.value.replace(/\s*\n+\s*/g, ' ');
      }
      if (searchClearButton) {
        searchClearButton.hidden = searchInput.value === '';
      }
      if (composing || event.isComposing) {
        return;
      }
      scheduleSearch();
    });

    searchInput.addEventListener('keydown', (event) => {
      // 変換中の Enter / Escape は IME の操作なので、検索操作として扱わない
      if (composing || event.isComposing || event.keyCode === 229) {
        return;
      }
      // textarea なので、Enter で改行が入らないように止める
      if (event.key === 'Enter') {
        event.preventDefault();
        if (searchTimeout) {
          clearTimeout(searchTimeout);
        }
        state.searchQuery = searchInput.value;
        renderAll();
        return;
      }
      if (event.key === 'Escape') {
        searchInput.value = '';
        state.searchQuery = '';
        if (searchClearButton) {
          searchClearButton.hidden = true;
        }
        renderAll();
        searchInput.blur();
      }
    });

    searchClearButton?.addEventListener('click', () => {
      searchInput.value = '';
      state.searchQuery = '';
      if (searchClearButton) {
        searchClearButton.hidden = true;
      }
      renderAll();
      searchInput.focus();
    });
  }

  // Escapeキーでタグブラウザを閉じる
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (fontSizePanel && !fontSizePanel.hidden) {
        fontSizeDialogController.close();
        return;
      }
      const panel = document.getElementById('tag-browser-panel');
      if (panel && !panel.hidden) {
        tagBrowserController?.closeTagBrowser();
      }
    }
  });

  loadPublicOutline();
})();
